import React, { useEffect, useState } from "react";
import { Award, BookX, ChevronUp, FileDown, FileText, Loader2, Trophy } from "lucide-react";
import { ApiService } from "../../../../services/api";
import { useAuth } from "../../../../contexts/AuthContext";
import type { StudentPracticeExamRow, StudentPracticeExamTest } from "../../../../types/practice-exam";
import { getStudentReportData, saveReportCardPdf } from "../../../practice-exams/ReportCards";
import WebStudentReportCard from "../../../practice-exams/components/WebStudentReportCard";

interface StudentPracticeExamsSectionProps {
  userId: string;
}

const SHORT_TEST_LABELS: Record<string, string> = {
  "türkçe": "Türkçe",
  "sosyal bilimler": "Sosyal",
  "temel matematik": "Matematik",
  "türk dili ve edebiyatı - sosyal bilimler-1": "Edeb.-Sos.1",
  "sosyal bilimler-2": "Sosyal-2",
  "matematik": "Matematik",
  "fen bilimleri": "Fen",
  "din kültürü ve ahlak bil.": "Din",
  "ingilizce": "İngilizce",
  "sosyal bilgiler t.c. ink. tar. ve ata.": "İnkılap",
};

const getShortTestLabel = (testName: string) => SHORT_TEST_LABELS[testName.toLocaleLowerCase("tr-TR").trim()] || testName;

const formatNet = (value: number | null | undefined) => (Number.isFinite(value) ? Number(value).toFixed(2) : "-");

const formatDate = (value?: string | null) => {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "-" : date.toLocaleDateString("tr-TR", { day: "2-digit", month: "short", year: "numeric" });
};

const netColor = (test: StudentPracticeExamTest) => {
  if (test.totalQuestions > 0 && test.unanswered === test.totalQuestions) return "bg-gray-100 text-gray-400";
  if (test.net < 0) return "bg-red-50 text-red-700";
  if (test.averageNet != null && test.net >= test.averageNet) return "bg-green-50 text-green-700";
  return "bg-amber-50 text-amber-700";
};

const TestChips = ({ label, tests }: { label?: string; tests: StudentPracticeExamTest[] }) => {
  if (tests.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-1">
      {label && <span className="mr-1 text-[11px] font-semibold uppercase text-gray-500">{label}</span>}
      {tests.map((test) => (
        <span
          key={test.testId}
          className={`rounded px-1.5 py-0.5 text-[11px] font-medium ${netColor(test)}`}
          title={`${test.testName}: ${test.correct} D · ${test.incorrect} Y · ${test.unanswered} B${test.averageNet != null ? ` · sınav ort. ${formatNet(test.averageNet)}` : ""}`}>
          {getShortTestLabel(test.testName)} {formatNet(test.net)}
        </span>
      ))}
    </div>
  );
};

export default function StudentPracticeExamsSection({ userId }: StudentPracticeExamsSectionProps) {
  const { systemConfig } = useAuth();
  const [rows, setRows] = useState<StudentPracticeExamRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedExam, setSelectedExam] = useState<StudentPracticeExamRow | null>(null);
  const [reportData, setReportData] = useState<any>(null);
  const [totalStudents, setTotalStudents] = useState(0);
  const [cardLoading, setCardLoading] = useState(false);
  const [pdfLoadingExamId, setPdfLoadingExamId] = useState<string | null>(null);
  const [bookletLoadingExamId, setBookletLoadingExamId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setSelectedExam(null);
    setReportData(null);
    setError(null);
    setLoading(true);

    ApiService.getStudentPracticeExams(userId)
      .then((res) => {
        if (!cancelled) setRows(res?.rows || []);
      })
      .catch((err: any) => {
        if (!cancelled) {
          setRows([]);
          setError(err?.message || "Deneme sonuçları yüklenemedi");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [userId]);

  const handleOpenCard = async (row: StudentPracticeExamRow) => {
    if (selectedExam?.examId === row.examId) {
      setSelectedExam(null);
      setReportData(null);
      return;
    }

    setSelectedExam(row);
    setReportData(null);
    setError(null);
    setCardLoading(true);
    try {
      const res = await ApiService.getStudentPracticeExamResult(userId, row.examId);
      const overallStats = res?.overallStats || {};
      setTotalStudents(Number(overallStats?.totalStudents) || 0);
      setReportData({
        ...getStudentReportData(res.result, {
          bookletMapping: res?.bookletMapping || {},
          testNames: res?.testNames || {},
          testAverages: res?.testAverages || {},
          overallStats,
          levelType: res?.exam?.levelType || row.levelType,
          yksMode: res?.exam?.yksMode || row.yksMode,
          systemType: res?.exam?.systemType || row.systemType,
        }),
        examId: row.examId,
        additionalInfo: res?.additionalInfo || null,
      });
    } catch (err: any) {
      setError(err?.message || "Karne yüklenemedi");
      setSelectedExam(null);
    } finally {
      setCardLoading(false);
    }
  };

  const handleDownloadPdf = async (row: StudentPracticeExamRow) => {
    setError(null);
    setPdfLoadingExamId(row.examId);
    try {
      saveReportCardPdf(await ApiService.downloadStudentPracticeExamReportCard(userId, row.examId));
    } catch (err: any) {
      setError(err?.message || "Karne PDF'i oluşturulamadı");
    } finally {
      setPdfLoadingExamId(null);
    }
  };

  const handleDownloadErrorBooklet = async (row: StudentPracticeExamRow) => {
    setError(null);
    setBookletLoadingExamId(row.examId);
    try {
      saveReportCardPdf(await ApiService.downloadStudentPracticeExamErrorBooklet(userId, row.examId));
    } catch (err: any) {
      setError(err?.message || "Hata kitapçığı oluşturulamadı");
    } finally {
      setBookletLoadingExamId(null);
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-3 sm:p-4">
      <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center">
        <Trophy className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
        Deneme Sonuçları
        {rows.length > 0 && ` (${rows.length})`}
      </h3>

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
          <span className="ml-2 text-gray-600">Deneme sonuçları yükleniyor...</span>
        </div>
      ) : error && rows.length === 0 ? (
        <div className="text-center py-6 text-sm text-red-600">{error}</div>
      ) : rows.length === 0 ? (
        <div className="text-center py-6 sm:py-8 text-gray-500 text-sm">Bu öğrenci henüz bir deneme sınavı tamamlamamış.</div>
      ) : (
        <div className="overflow-x-auto -mx-3 sm:mx-0">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-2 sm:px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Deneme</th>
                <th className="px-2 sm:px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Netler</th>
                <th className="px-2 sm:px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Net · Başarı</th>
                <th className="px-2 sm:px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Puan</th>
                <th className="px-2 sm:px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Sıra</th>
                <th className="px-2 sm:px-3 py-2" />
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {rows.map((row) => {
                const isYks = row.levelType === "yks";
                const tytTests = row.tests.filter((test) => test.session === "tyt");
                const aytTests = row.tests.filter((test) => test.session === "ayt");
                const hasAyt = aytTests.some((test) => test.correct + test.incorrect > 0);
                const isOpen = selectedExam?.examId === row.examId;

                return (
                  <tr key={row.examId} className={isOpen ? "bg-blue-50/40" : "hover:bg-gray-50"}>
                    <td className="px-2 sm:px-3 py-2 align-top">
                      <div className="text-sm font-medium text-gray-900">{row.examName}</div>
                      <div className="text-xs text-gray-500">
                        Çözdüğü: {formatDate(row.submittedAt)}
                        {row.examDate && ` · Deneme tarihi: ${formatDate(row.examDate)}`}
                      </div>
                    </td>
                    <td className="px-2 sm:px-3 py-2 align-top">
                      <div className="space-y-1">
                        {isYks ? (
                          <>
                            <TestChips label="TYT" tests={tytTests} />
                            {hasAyt ? (
                              <TestChips label="AYT" tests={aytTests} />
                            ) : (
                              <div className="text-[11px] text-gray-400">AYT çözülmedi</div>
                            )}
                          </>
                        ) : (
                          <TestChips tests={row.tests} />
                        )}
                      </div>
                    </td>
                    <td className="px-2 sm:px-3 py-2 align-top text-center">
                      {/* Soru sayısı farklı denemeler yüzdeyle kıyaslanır; net yalnız sorumlu olunan testlerden (alan dışı sayılmaz) */}
                      <div className="text-sm font-semibold text-gray-900">
                        {formatNet(row.scope?.net ?? row.totalNet)}
                        {row.scope?.questions ? <span className="text-xs font-normal text-gray-500"> / {row.scope.questions}</span> : null}
                      </div>
                      {row.scope?.success != null ? (
                        <div className="text-[11px] font-medium text-blue-700">%{row.scope.success.toLocaleString("tr-TR", { maximumFractionDigits: 1 })}{row.scope.label ? ` · ${row.scope.label}` : ""}</div>
                      ) : null}
                      <div className="text-[11px] text-gray-500">
                        {row.correct} D · {row.incorrect} Y · {row.unanswered} B
                      </div>
                    </td>
                    <td className="px-2 sm:px-3 py-2 align-top text-center">
                      {row.yks?.primaryScore != null ? (
                        <>
                          <div className="text-sm font-semibold text-purple-700">{formatNet(row.yks.primaryScore)}</div>
                          <div className="text-[11px] text-gray-500">{row.yks.primaryScoreType === "TYT" ? "TYT" : row.yks.primaryScoreType}</div>
                        </>
                      ) : row.alanPuani?.score != null ? (
                        <>
                          <div className="text-sm font-semibold text-purple-700">{formatNet(row.alanPuani.score)}</div>
                          <div className="text-[11px] text-gray-500">{row.alanPuani.label || row.alanPuani.alan} (alan puanı)</div>
                        </>
                      ) : (
                        <span className="text-sm text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-2 sm:px-3 py-2 align-top text-center">
                      {row.yks?.ranking?.rank ? (
                        <span className="inline-flex items-center gap-1 text-sm text-gray-700">
                          <Award className="w-3.5 h-3.5 text-amber-500" />
                          {row.yks.ranking.rank} / {row.yks.ranking.participantCount}
                        </span>
                      ) : (
                        <span className="text-sm text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-2 sm:px-3 py-2 align-top text-right">
                      <div className="inline-flex flex-wrap justify-end gap-1">
                        <button
                          onClick={() => handleOpenCard(row)}
                          className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50">
                          {isOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <FileText className="w-3.5 h-3.5" />}
                          {isOpen ? "Kapat" : "Karne"}
                        </button>
                        <button
                          onClick={() => handleDownloadPdf(row)}
                          disabled={pdfLoadingExamId === row.examId}
                          title="Detaylı karne analizini PDF olarak indir"
                          className="inline-flex items-center gap-1 rounded-md border border-indigo-200 bg-indigo-50 px-2 py-1 text-xs font-medium text-indigo-700 hover:bg-indigo-100 disabled:opacity-60">
                          {pdfLoadingExamId === row.examId ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5" />}
                          PDF
                        </button>
                        {row.levelType === "yks" && row.systemType === "internal" && (
                          <button
                            onClick={() => handleDownloadErrorBooklet(row)}
                            disabled={bookletLoadingExamId === row.examId}
                            title="Denemede kaçırılan kazanımlardan çalışma kitapçığı (PDF)"
                            className="inline-flex items-center gap-1 rounded-md border border-rose-200 bg-rose-50 px-2 py-1 text-xs font-medium text-rose-700 hover:bg-rose-100 disabled:opacity-60">
                            {bookletLoadingExamId === row.examId ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <BookX className="w-3.5 h-3.5" />}
                            Hata
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {(bookletLoadingExamId || pdfLoadingExamId) && (
        <div className="mt-3 flex items-center text-sm text-gray-600">
          <Loader2 className="w-4 h-4 animate-spin text-blue-600 mr-2" />
          {bookletLoadingExamId
            ? "Hata kitapçığı hazırlanıyor: kaçırılan sorulara göre çalışma soruları seçiliyor, birkaç saniye sürebilir. Hazır olunca PDF iner."
            : "Karne PDF'i hazırlanıyor, hazır olunca iner."}
        </div>
      )}

      {error && rows.length > 0 && <div className="mt-3 text-sm text-red-600">{error}</div>}

      {selectedExam && (
        <div className="mt-4 border-t border-gray-200 pt-4">
          {cardLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
              <span className="ml-2 text-gray-600">Karne yükleniyor...</span>
            </div>
          ) : reportData ? (
            <WebStudentReportCard
              data={reportData}
              examName={selectedExam.examName}
              companyLogo={systemConfig?.companyLogo || undefined}
              companyName={systemConfig?.companyName || undefined}
              totalStudents={totalStudents}
            />
          ) : null}
        </div>
      )}
    </div>
  );
}
